import { supabase } from '../lib/supabase';
import { getCachedData, invalidateCache, CACHE_KEYS, CACHE_TTL } from '../utils/cache';
import { isIdentityDocumentCurrentlyValid } from './identityDocumentService';
import { getExperienceLevelLabelAr, normalizeExperienceLevelToUi } from '../utils/experienceLevel';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const photographerService = {
  // Get all photographers — with optional short-lived cache (2 min) to reduce DB roundtrips
  async getAllPhotographers(skipCache = false) {
    if (skipCache) {
      return this._fetchAllPhotographers();
    }
    return getCachedData(
      CACHE_KEYS.PHOTOGRAPHERS_LIST,
      () => this._fetchAllPhotographers(),
      2 * 60 * 1000 // 2 minutes — safe TTL that still feels real-time
    );
  },

  // Internal function to fetch photographers from DB
  async _fetchAllPhotographers() {
    try {
      await supabase.rpc('revoke_expired_identity_verifications');
    } catch (e) {
      console.warn('⚠️ Could not run identity expiry revocation:', e?.message || e);
    }

    const baseQuery = () =>
      supabase
        .from('photographer_profiles')
        .select('*, shooting_methods')
        .eq('available_for_booking', true)
        .eq('identity_verified', true)
        .eq('verification_status', 'approved')
        .or('status.eq.approved,status.is.null')
        .order('rating', { ascending: false });

    // حاول أولاً مع فلتر نسبة الاكتمال 100% وإخفاء المصورين المعلقين بسبب الانسحابات
    const now = new Date().toISOString();
    // يُخفى المصور إذا كانت عقوبة الإخفاء سارية أو كان التعليق بسبب الانسحاب سارياً
    let { data: photographers, error } = await baseQuery()
      .eq('profile_completion_pct', 100)
      .or(`visibility_penalty_until.is.null,visibility_penalty_until.lt.${now}`)
      .or(`withdrawal_suspended_until.is.null,withdrawal_suspended_until.lt.${now}`);

    // إذا فشل (العمود غير موجود في قاعدة البيانات بعد)، أعد المحاولة بدون الفلتر
    if (error) {
      const isColumnMissing =
        error.code === '42703' ||
        (error.message && error.message.includes('profile_completion_pct'));

      if (isColumnMissing) {
        console.warn('⚠️ profile_completion_pct column not found, fetching without completion filter');
        const fallback = await baseQuery()
          .or(`visibility_penalty_until.is.null,visibility_penalty_until.lt.${now}`)
          .or(`withdrawal_suspended_until.is.null,withdrawal_suspended_until.lt.${now}`);
        photographers = fallback.data;
        if (fallback.error) {
          console.error('❌ Error fetching photographers:', fallback.error);
          throw fallback.error;
        }
      } else {
        console.error('❌ Error fetching photographers:', error);
        throw error;
      }
    }
    
    console.log('📸 getAllPhotographers - Raw data from DB:', {
      count: photographers?.length || 0,
      sample: photographers?.[0] ? {
        user_id: photographers[0].user_id,
        available_for_booking: photographers[0].available_for_booking,
        status: photographers[0].status,
        experience_years: photographers[0].experience_years, // Added for debugging
        experience_level: photographers[0].experience_level, // Added for debugging
        service_center_lat: photographers[0].service_center_lat,
        service_center_lng: photographers[0].service_center_lng,
        service_radius_km: photographers[0].service_radius_km,
        base_lat: photographers[0].base_lat,
        base_lng: photographers[0].base_lng
      } : null
    });
    
    if (!photographers || photographers.length === 0) {
      console.warn('⚠️ No photographers found in database');
      return [];
    }

    photographers = photographers.filter((p) => isIdentityDocumentCurrentlyValid(p));

    // ── إخفاء المصور إذا كان لديه حجز مؤكد ومدفوع في نفس اليوم ──────────────
    try {
      const todayRiyadh = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
      const { data: busyBookings } = await supabase
        .from('bookings')
        .select('photographer_id, assigned_photographer_id')
        .eq('status', 'paid_confirmed')
        .eq('booking_date', todayRiyadh);

      if (busyBookings && busyBookings.length > 0) {
        const busyIds = new Set();
        busyBookings.forEach(b => {
          if (b.photographer_id) busyIds.add(b.photographer_id);
          if (b.assigned_photographer_id) busyIds.add(b.assigned_photographer_id);
        });
        const before = photographers.length;
        photographers = photographers.filter(p => !busyIds.has(p.user_id));
        console.log(`📸 Filtered out ${before - photographers.length} photographer(s) with paid_confirmed booking today`);
      }
    } catch (busyErr) {
      // لا توقف العرض إذا فشل هذا الفلتر
      console.warn('⚠️ Could not check today\'s busy photographers:', busyErr);
    }

    // Get user profiles
    const userIds = photographers.map(p => p.user_id).filter(Boolean);
    let userProfiles = {};
    
    if (userIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, avatar_url')
        .in('id', userIds);

      if (!profilesError && profiles) {
        profiles.forEach(profile => {
          userProfiles[profile.id] = profile;
        });
      }
    }

    // Combine photographers with user profiles
    const enrichedPhotographers = photographers.map(photographer => ({
      ...photographer,
      user: userProfiles[photographer.user_id] || null
    }));

    const result = toCamelCase(enrichedPhotographers);
    
    // Ensure shooting_methods is available as shootingMethods for all photographers
    const normalizedResult = result.map(p => {
      // Ensure shooting_methods is converted to shootingMethods
      if (p.shooting_methods && !p.shootingMethods) {
        p.shootingMethods = p.shooting_methods;
      } else if (!p.shootingMethods && !p.shooting_methods) {
        p.shootingMethods = [];
      }
      // Ensure it's always an array
      if (!Array.isArray(p.shootingMethods)) {
        p.shootingMethods = p.shootingMethods ? [p.shootingMethods] : [];
      }
      return p;
    });
    
    console.log('📸 getAllPhotographers result:', {
      count: normalizedResult.length,
      sample: normalizedResult[0] ? {
        id: normalizedResult[0].userId || normalizedResult[0].user_id,
        shootingMethods: normalizedResult[0].shootingMethods,
        shooting_methods: normalizedResult[0].shooting_methods,
        isArray: Array.isArray(normalizedResult[0].shootingMethods),
        // Location data debugging
        serviceCenterLat: normalizedResult[0].serviceCenterLat,
        serviceCenterLng: normalizedResult[0].serviceCenterLng,
        service_center_lat: normalizedResult[0].service_center_lat,
        service_center_lng: normalizedResult[0].service_center_lng,
        serviceRadiusKm: normalizedResult[0].serviceRadiusKm,
        service_radius_km: normalizedResult[0].service_radius_km
      } : null
    });
    
    return normalizedResult;
  },

  // Get photographer by ID (supports both photographer_profiles.id and user_id)
  async getPhotographer(photographerId) {
    if (!photographerId) {
      console.error('getPhotographer: photographerId is required');
      throw new Error('معرف المصور مطلوب');
    }

    try {
      // Try to find by user_id first (most common case)
      let { data: photographer, error } = await supabase
        .from('photographer_profiles')
        .select('*')
        .eq('user_id', photographerId)
        .single();

      // If not found by user_id, try by id (photographer_profiles.id)
      if (error && error.code === 'PGRST116') {
        console.log('📸 Not found by user_id, trying by id:', photographerId);
        const { data: photographerById, error: errorById } = await supabase
          .from('photographer_profiles')
          .select('*')
          .eq('id', photographerId)
          .single();
        
        if (!errorById && photographerById) {
          photographer = photographerById;
          error = null;
        } else {
          error = errorById || error;
        }
      }

      if (error) {
        console.error('Error fetching photographer profile:', error);
        // If photographer profile doesn't exist, check if user exists
        if (error.code === 'PGRST116') {
          // No rows returned - check if user exists
          const { data: userCheck } = await supabase
            .from('user_profiles')
            .select('id, role')
            .eq('id', photographerId)
            .single();
          
          if (userCheck && userCheck.role !== 'photographer') {
            throw new Error('المستخدم المحدد ليس مصوراً');
          }
        }
        throw error;
      }

      if (!photographer) {
        console.warn('Photographer profile not found for ID:', photographerId);
        return null;
      }

      // Get user profile
      let userProfile = null;
      if (photographer.user_id) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url')
          .eq('id', photographer.user_id)
          .single();

        if (profileError) {
          console.warn('Error fetching user profile:', profileError);
        } else if (profile) {
          userProfile = profile;
        }
      }

      // Combine photographer with user profile
      const enrichedPhotographer = {
        ...photographer,
        user: userProfile
      };

      // Preserve shooting_method_models raw (snake_case keys) before toCamelCase converts them
      const rawShootingMethodModels = photographer.shooting_method_models || {};

      const result = toCamelCase(enrichedPhotographer);

      // Restore shooting_method_models with original snake_case keys so they match method values
      // (professional_camera / mobile_camera / drone) used throughout the UI
      result.shootingMethodModels = rawShootingMethodModels;
      result.shooting_method_models = rawShootingMethodModels;
      
      // Ensure shooting_methods is available as shootingMethods (toCamelCase should handle this, but ensure it)
      if (result.shooting_methods && !result.shootingMethods) {
        result.shootingMethods = result.shooting_methods;
      } else if (!result.shootingMethods && !result.shooting_methods) {
        result.shootingMethods = [];
      }
      
      // Debug: Log shooting_methods conversion
      console.log('📸 getPhotographer shooting_methods debug:', {
        photographerId,
        beforeConversion: {
          shooting_methods: photographer.shooting_methods,
          shootingMethods: photographer.shootingMethods
        },
        afterConversion: {
          shootingMethods: result.shootingMethods,
          shooting_methods: result.shooting_methods
        },
        resultKeys: Object.keys(result).filter(k => k.includes('shooting'))
      });
      
      return result;
    } catch (error) {
      console.error('getPhotographer error:', error);
      throw error;
    }
  },

  // Get photographer stats
  async getPhotographerStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Get total bookings count
      const { count: totalBookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', user?.id);

      if (bookingsError && !bookingsError.message?.includes('does not exist')) {
        throw bookingsError;
      }

      // Get completed bookings
      const { count: completedBookings, error: completedError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', user?.id)
        .eq('status', 'completed');

      if (completedError && !completedError.message?.includes('does not exist')) {
        throw completedError;
      }

      // Get average rating
      let reviews = [];
      try {
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('rating')
          .eq('photographer_id', user?.id);

        if (!reviewsError && reviewsData) {
          reviews = reviewsData;
        }
      } catch (reviewsErr) {
        console.warn('Failed to load reviews:', reviewsErr);
      }

      const avgRating = reviews?.length > 0
        ? (reviews?.reduce((sum, r) => sum + (r?.rating || 0), 0) / reviews?.length)?.toFixed(1)
        : '0.0';

      // Get monthly bookings
      const currentMonth = new Date()?.toISOString()?.slice(0, 7);
      const { count: monthlyBookings, error: monthlyError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', user?.id)
        .gte('created_at', `${currentMonth}-01`);

      if (monthlyError && !monthlyError.message?.includes('does not exist')) {
        throw monthlyError;
      }

      return {
        totalBookings: totalBookings || 0,
        completedBookings: completedBookings || 0,
        completionRate: (totalBookings || 0) > 0 ? Math.round(((completedBookings || 0) / (totalBookings || 1)) * 100) : 0,
        avgRating,
        reviewCount: reviews?.length || 0,
        monthlyBookings: monthlyBookings || 0
      };
    } catch (error) {
      console.warn('Error loading photographer stats:', error);
      // Return default values if tables don't exist
      return {
        totalBookings: 0,
        completedBookings: 0,
        completionRate: 0,
        avgRating: '0.0',
        reviewCount: 0,
        monthlyBookings: 0
      };
    }
  },

  // Invalidate photographers cache (call after updates)
  invalidateCache() {
    invalidateCache(CACHE_KEYS.PHOTOGRAPHERS_LIST);
  },

  /**
   * Fetch alternative photographers matching booking criteria.
   * Respects each photographer's service radius (km) when precise location is available.
   * Falls back to city-name matching when coordinates are not available.
   *
   * Used by BookingWaiting when the current photographer declines or doesn't respond.
   */
  async fetchAlternativePhotographers({
    city,
    photographyType,
    shootingMethods = [],
    experienceLevel,
    excludeIds = [],
    preciseLocation = null, // { lat, lng } — booking's precise location
    bookingDate = null,     // 'YYYY-MM-DD' — exclude photographers busy on this date
  }) {
    // Haversine distance (km) — inlined to avoid cross-service import side-effects
    const _haversine = (lat1, lng1, lat2, lng2) => {
      const R = 6371;
      const toRad = (d) => d * (Math.PI / 180);
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    try {
      const all = await this._fetchAllPhotographers();

      const bookingCity = (city || '').trim();
      const bookingType = (photographyType || '').toLowerCase().trim();
      const bookingMethods = Array.isArray(shootingMethods) ? shootingMethods : [];
      const excludeSet = new Set(excludeIds.filter(Boolean));

      // ── استبعاد المصورين المشغولين في تاريخ الحجز ──────────────────────────
      if (bookingDate) {
        try {
          const { data: busyBookings } = await supabase
            .from('bookings')
            .select('photographer_id, assigned_photographer_id')
            .eq('status', 'paid_confirmed')
            .eq('booking_date', bookingDate);
          if (busyBookings) {
            busyBookings.forEach((b) => {
              if (b.photographer_id) excludeSet.add(b.photographer_id);
              if (b.assigned_photographer_id) excludeSet.add(b.assigned_photographer_id);
            });
          }
        } catch (_) {
          // non-blocking — proceed without date filtering if query fails
        }
      }

      // Resolved booking coordinates
      const bookingLat = preciseLocation?.lat ? parseFloat(preciseLocation.lat) : null;
      const bookingLng = preciseLocation?.lng ? parseFloat(preciseLocation.lng) : null;
      const hasPreciseLocation = bookingLat !== null && bookingLng !== null && !isNaN(bookingLat) && !isNaN(bookingLng);

      const DEFAULT_SERVICE_RADIUS_KM = 50;

      const targetExperienceUi = normalizeExperienceLevelToUi(experienceLevel);

      const scored = all
        .filter(p => {
          const pid = p.userId || p.user_id;
          if (!pid) return false;
          if (excludeSet.has(pid)) return false;

          // ── Service-radius check (only when booking has precise coordinates) ──
          if (hasPreciseLocation) {
            const centerLat = parseFloat(p.serviceCenterLat || p.service_center_lat);
            const centerLng = parseFloat(p.serviceCenterLng || p.service_center_lng);

            if (!isNaN(centerLat) && !isNaN(centerLng)) {
              const radiusKm = parseFloat(p.serviceRadiusKm || p.service_radius_km) || DEFAULT_SERVICE_RADIUS_KM;
              const dist = _haversine(bookingLat, bookingLng, centerLat, centerLng);
              if (dist > radiusKm) return false; // outside photographer's coverage zone
            }
            // If photographer hasn't set a service center, fall through to city matching
          }

          if (targetExperienceUi) {
            const pExpLevel = p.experienceLevel || p.experience_level || null;
            const pExpUi = normalizeExperienceLevelToUi(pExpLevel);
            if (pExpUi && pExpUi !== targetExperienceUi) return false;
          }

          return true;
        })
        .map(p => {
          const pid = p.userId || p.user_id;
          const pCity = (p.city || '').trim();
          const pSpecializations = Array.isArray(p.specializations) ? p.specializations : [];
          const pMethods = Array.isArray(p.shootingMethods) ? p.shootingMethods : (Array.isArray(p.shooting_methods) ? p.shooting_methods : []);
          const pExpLevel = p.experienceLevel || p.experience_level || null;
          const pExpYears = p.experienceYears ?? p.experience_years ?? 0;

          let score = 0;
          let distanceKm = null;

          if (hasPreciseLocation) {
            const centerLat = parseFloat(p.serviceCenterLat || p.service_center_lat);
            const centerLng = parseFloat(p.serviceCenterLng || p.service_center_lng);
            if (!isNaN(centerLat) && !isNaN(centerLng)) {
              distanceKm = _haversine(bookingLat, bookingLng, centerLat, centerLng);
              // Closer photographers score higher (up to +40 for 0 km, 0 for 50+ km)
              score += Math.max(0, 40 - distanceKm * 0.8);
            } else {
              // No service center set — use city fallback scoring
              if (bookingCity && pCity === bookingCity) score += 30;
            }
          } else {
            // No precise location — use city name matching
            if (bookingCity && pCity === bookingCity) score += 40;
            else if (bookingCity && pCity) score += 5;
          }

          // Photography type match
          if (bookingType) {
            const typeMatches = pSpecializations.some(s =>
              s.toLowerCase().includes(bookingType) || bookingType.includes(s.toLowerCase())
            );
            if (typeMatches) score += 30;
          }

          // Shooting method match
          if (bookingMethods.length > 0) {
            const methodMatches = bookingMethods.filter(m => pMethods.includes(m)).length;
            score += methodMatches * 15;
          }

          // Rating bonus
          score += parseFloat(p.rating || 0) * 2;

          return {
            id: pid,
            name: p.user?.fullName || p.user?.full_name || 'مصور',
            avatar: p.user?.avatarUrl || p.user?.avatar_url || null,
            rating: parseFloat(p.rating || 0),
            reviewCount: p.totalReviews || p.total_reviews || 0,
            city: pCity,
            specialties: pSpecializations.slice(0, 3),
            experienceLevel: getExperienceLevelLabelAr(pExpLevel || (pExpYears >= 8 ? 'elite' : pExpYears >= 4 ? 'distinguished' : 'certified')),
            shootingMethods: pMethods,
            bio: p.bio || '',
            isVerified: p.status === 'approved',
            distanceKm: distanceKm !== null ? Math.round(distanceKm * 10) / 10 : null,
            score,
          };
        })
        .filter(p => p.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);

      return scored;
    } catch (err) {
      console.warn('fetchAlternativePhotographers error:', err);
      return [];
    }
  },

  /**
   * تحديث المعلومات البنكية للمصور (لتحويل الأرباح)
   */
  async updateBankInfo(userId, { bankName, bankAccountHolderName, iban }) {
    const { data, error } = await supabase
      .from('photographer_profiles')
      .update({
        bank_name: bankName || null,
        bank_account_holder_name: bankAccountHolderName || null,
        iban: iban || null,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .select('bank_name, bank_account_holder_name, iban')
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },
};